import type { Clip, Segment, Word } from "./clip.js";

/** Bump when the detection below changes: clips carrying an older number are re-scanned when opened. */
export const QURAN_DETECTOR_VERSION = 2;

/** Lower-case, no diacritics/apostrophes/hyphens, single spaces: "Alhamdulillāhi Rabbil-ʿĀlamīn" → "alhamdulillahi rabbil alamin". */
export function normalise(text: string): string {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[’‘'`ʿʾ]/g, "").replace(/[-–—_]/g, " ").replace(/[^a-z0-9\u0600-\u06FF\s]/g, " ").replace(/\s+/g, " ").trim();
}

/** Transliterated recitation and Quran words as they come out of the transcriber (matched on the normalised text). */
const RECITED = [
  "alhamdulillahi rabbil", "alhamdulillah rabbil", "rabbil alameen", "rabbil alamin", "rabb il alamin",
  "ar rahman ar rahim", "arrahman arrahim", "ar rahmanir rahim", "arrahmanir rahim", "ar rahman ar raheem", "arrahman arraheem",
  "maliki yawm", "maliki yaum", "malik yawm", "iyyaka nabudu", "iyyaka na budu", "iyyaka nastain", "iyyaka nasta in", "ihdinas sirat", "ihdina", "siratal mustaqim", "sirat al mustaqim",
  "bismillah", "qul huwallahu", "qul huwa allahu", "audhu billah", "authu billah", "la ilaha illallah", "la ilaha illa allah", "inna lillahi", "rabbana atina", "hasbunallah", "ayatul kursi", "ayat al kursi", "ayatal kursi",
  "al fatiha", "fatiha", "fatihah", "surah", "surat", "sura ", "ayah", "ayat", "verse", "quran", "quranic", "hadith qudsi", "kalam allah", "kalamullah",
];
/** Phrases that introduce or quote the words of Allah. */
const CUES = [
  /\ballah (?:says|said|tells|told|mentions|mentioned|describes|described|commands|commanded|promises|promised|swears|swore|answers|answered|replies|replied)\b/,
  /\b(?:says|said) allah\b/, /\b(?:the )?(?:most high|exalted|almighty) (?:says|said)\b/, /\bhe (?:says|said) in (?:the quran|surah|surat)\b/,
  /\bmy servant\b/, /\bmy slave\b/, /\b\d{1,3} \d{1,3}\b(?=.*\b(?:surah|surat|verse|ayah)\b)/,
];
/** A verse reference like [2:286] survives normalisation as "2 286" only inside a sentence that also names the surah; match the raw form too. */
const REF = /\[\d{1,3}:\d{1,3}(?:-\d{1,3})?\]/;
/** Arabic-script text that reads like recitation (a long Arabic run). */
const ARABIC_RUN = /[\u0600-\u06FF]{2,}(?:\s+[\u0600-\u06FF]{2,}){4,}/;

export interface SentenceLike { startMs: number; endMs: number; text: string }

export function isQuranText(text: string): boolean {
  if (REF.test(text) || ARABIC_RUN.test(text)) return true;
  const n = ` ${normalise(text)} `;
  return RECITED.some((p) => n.includes(p.startsWith(" ") ? p : ` ${p}`) || n.includes(p)) || CUES.some((re) => re.test(n));
}

/** Source ranges where the Quran is recited or quoted, from the sentences' text; neighbouring hits merge. */
export function findQuranSpans(sentences: SentenceLike[], opts: { padMs?: number } = {}): Segment[] {
  const pad = opts.padMs ?? 250;
  const hits = sentences.filter((s) => isQuranText(s.text));
  const out: Segment[] = [];
  for (const h of hits) {
    const seg = { inMs: Math.max(0, h.startMs - pad), outMs: h.endMs + pad };
    const last = out[out.length - 1];
    if (last && seg.inMs <= last.outMs + 1500) last.outMs = Math.max(last.outMs, seg.outMs);
    else out.push(seg);
  }
  return out;
}

/** Timeline ranges (after cuts and rearrangement) covered by any of the given source ranges. */
export function sourceRangesToTimeline(segments: Segment[], ranges: Segment[]): Segment[] {
  const out: Segment[] = [];
  let acc = 0;
  for (const s of segments) {
    for (const r of ranges) {
      const a = Math.max(s.inMs, r.inMs), b = Math.min(s.outMs, r.outMs);
      if (b > a) out.push({ inMs: acc + (a - s.inMs), outMs: acc + (b - s.inMs) });
    }
    acc += s.outMs - s.inMs;
  }
  return out.sort((x, y) => x.inMs - y.inMs);
}

/** 0..1 music multiplier at a timeline moment: 0 inside a mute range, easing over rampMs at each edge. */
export function muteGainAt(mutes: Segment[], ms: number, rampMs = 350): number {
  let g = 1;
  for (const m of mutes) {
    if (ms >= m.inMs && ms <= m.outMs) return 0;
    if (ms < m.inMs && m.inMs - ms < rampMs) g = Math.min(g, (m.inMs - ms) / rampMs);
    if (ms > m.outMs && ms - m.outMs < rampMs) g = Math.min(g, (ms - m.outMs) / rampMs);
  }
  return g;
}

/** Merges overlapping/touching ranges. */
export function mergeRanges(ranges: Segment[]): Segment[] {
  const sorted = [...ranges].sort((a, b) => a.inMs - b.inMs);
  const out: Segment[] = [];
  for (const r of sorted) {
    const last = out[out.length - 1];
    if (last && r.inMs <= last.outMs) last.outMs = Math.max(last.outMs, r.outMs);
    else out.push({ ...r });
  }
  return out;
}

/** When the Quran runs through most of a clip, a bed that keeps dipping in and out is worse than none: the whole clip goes quiet.
 *  Rule: quiet ranges cover ≥ 35 % of the timeline, or there are 3+ of them and the bed would never get 8 s to itself. */
export const WHOLE_CLIP_COVERAGE = 0.35;
export const WHOLE_CLIP_MIN_GAP_MS = 8000;
export function quietWholeClip(totalMs: number, timelineMutes: Segment[]): boolean {
  if (!timelineMutes.length || totalMs <= 0) return false;
  const merged = mergeRanges(timelineMutes);
  const covered = merged.reduce((a, r) => a + (r.outMs - r.inMs), 0);
  if (covered / totalMs >= WHOLE_CLIP_COVERAGE) return true;
  if (merged.length < 3) return false;
  let maxGap = merged[0].inMs;
  for (let i = 1; i < merged.length; i++) maxGap = Math.max(maxGap, merged[i].inMs - merged[i - 1].outMs);
  maxGap = Math.max(maxGap, totalMs - merged[merged.length - 1].outMs);
  return maxGap < WHOLE_CLIP_MIN_GAP_MS;
}

export interface TimelineMutes { ranges: Segment[]; whole: boolean; quranCount: number }

/** The quiet ranges on the clip's timeline: the owner's own mutes plus the detected Quran spans (when quranMute is on), and whether the whole clip should stay quiet. */
export function timelineMusicMutes(clip: Pick<Clip, "segments" | "edits" | "music" | "quranSpans" | "direction">): TimelineMutes {
  const own = [...(clip.edits?.musicMutes ?? []), ...clip.direction.moments.filter((m) => m.kind === "quiet").map((m) => ({ inMs: m.atMs, outMs: m.endMs ?? m.atMs + 4000 }))];
  const quran = clip.music.quranMute ? sourceRangesToTimeline(clip.segments, clip.quranSpans) : [];
  const ranges = mergeRanges([...sourceRangesToTimeline(clip.segments, own), ...quran]);
  const totalMs = clip.segments.reduce((a, s) => a + (s.outMs - s.inMs), 0);
  return { ranges, whole: quietWholeClip(totalMs, quran), quranCount: mergeRanges(quran).length };
}

export const wordsToSentences = (words: Word[]): SentenceLike[] => words.map((w) => ({ startMs: w.startMs, endMs: w.endMs, text: w.text }));
